import * as React from 'react'
import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { AllowlistDto, AllowlistEntryDto, CapturedMailDto, CapturedMailListDto } from '@lib/dto/test-mail.dto'
import type { AddAllowlistEntryInput } from '@lib/input/test-mail.input'
import { useDashboardScope } from '@/dashboard/DashboardScope'
import { useRefreshOnForbidden } from '@/dashboard/useRefreshOnForbidden'
import { teamKeys, testMailKeys } from '@/hooks/queryKeys'
import { TestMailService } from '@/services/TestMailService'
import type { Uuid } from '@lib/validators/params'

function useScopedTestMailService(): TestMailService {
  const { client } = useDashboardScope()
  return React.useMemo(() => new TestMailService(client), [client])
}

/** The newest captured test emails, optionally for one envelope. */
export function useMailbox(envelopeId: Uuid | null): UseQueryResult<CapturedMailListDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedTestMailService()
  return useQuery({
    queryKey: testMailKeys.messages(tenantId, cacheKey, envelopeId),
    queryFn: () => service.listMessages(tenantId, envelopeId ? { envelopeId } : {}),
  })
}

export function useCapturedMail(messageId: Uuid | null): UseQueryResult<CapturedMailDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedTestMailService()
  return useQuery({
    queryKey: testMailKeys.message(tenantId, cacheKey, messageId),
    queryFn: messageId !== null ? () => service.getMessage(tenantId, messageId) : skipToken,
  })
}

export function useAllowlist(): UseQueryResult<AllowlistDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedTestMailService()
  return useQuery({ queryKey: testMailKeys.allowlist(tenantId, cacheKey), queryFn: () => service.allowlist(tenantId) })
}

/** Allowlist changes; each refreshes the allowlist and the activity log. */
export function useAllowlistActions(): {
  add: UseMutationResult<AllowlistEntryDto, Error, AddAllowlistEntryInput>
  verify: UseMutationResult<AllowlistEntryDto, Error, Uuid>
  resend: UseMutationResult<AllowlistEntryDto, Error, Uuid>
  remove: UseMutationResult<void, Error, Uuid>
} {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedTestMailService()
  const queryClient = useQueryClient()
  const onError = useRefreshOnForbidden()
  const onSettled = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: testMailKeys.allowlist(tenantId, cacheKey) }),
      queryClient.invalidateQueries({ queryKey: teamKeys.activity(tenantId, cacheKey) }),
    ])
  }
  return {
    add: useMutation({ mutationFn: (input) => service.addEntry(tenantId, input), onError, onSettled }),
    verify: useMutation({ mutationFn: (id) => service.verifyEntry(tenantId, id), onError, onSettled }),
    resend: useMutation({ mutationFn: (id) => service.resendConfirmation(tenantId, id), onError, onSettled }),
    remove: useMutation({ mutationFn: (id) => service.removeEntry(tenantId, id), onError, onSettled }),
  }
}
